import { useState } from 'react';
import { t } from '@app/i18n';
import { Alert, Badge, Button, Card, ConfirmDialog, useToast } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import { useCanOperar } from '@app/auth/useCan';
import { errorView } from '@app/errors';
import { useDeployIdentityImage, type OrderResult, type RuntimeStatus } from './Runtime.api';
import s from './Runtime.module.css';

/**
 * Card **Aplicar imagem no Deployment** de T15 (FR-093, admin).
 *
 * É a única escrita da tela que muda o cluster diretamente, e é a saída do caso
 * "aprovada aguardando deploy": o contrato aprovou um digest que o Deployment
 * ainda não usa. Sem digest aprovado não há o que aplicar.
 */

export interface DeployCardProps {
  status?: RuntimeStatus;
}

export function DeployCard({ status }: DeployCardProps) {
  const identityId = status?.identity_id ?? '';
  const apply = useDeployIdentityImage(identityId);
  const canApply = useCanOperar('deployIdentityImage');
  const toast = useToast();

  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<ApiError | null>(null);
  const [resultado, setResultado] = useState<OrderResult | null>(null);

  if (!status || !canApply) return null;

  const alvo = `${status.namespace ?? '—'}/${status.slug ?? '—'}`;
  const withoutDigest = !status.approved_digest;

  async function confirm() {
    setErro(null);
    try {
      const output = await apply.mutateAsync();
      setResultado(output);
      setConfirmando(false);
      toast.mostrar(t('runtimeStatus.deploy.applied', { target: alvo }), { tone: 'sucesso' });
    } catch (falha) {
      setErro(falha as ApiError);
    }
  }

  return (
    <Card
      title={t('runtimeStatus.deploy.title')}
      subtitle={alvo}
      tip={t('runtimeStatus.deploy.tip')}
      toolbar={
        <Button icon="play" disabled={withoutDigest} onClick={() => setConfirmando(true)}>
          {t('runtimeStatus.deploy.apply')}
        </Button>
      }
    >
      <p className={s.estadoLinha}>
        <span className={s.checkRotulo}>{t('runtimeStatus.deploy.approvedDigest')}</span>
        {status.approved_digest ? (
          <span className={s.mono}>{status.approved_digest}</span>
        ) : (
          <span className={s.vazioTexto}>{t('runtimeStatus.deploy.noDigest')}</span>
        )}
      </p>

      {status.checks.release_matches === false ? (
        <p className={s.estadoDetalhe}>{t('runtimeStatus.deploy.podOutOfDigest')}</p>
      ) : null}

      {resultado ? (
        <ul className={s.passos} aria-label={t('runtimeStatus.deploy.steps.label')}>
          {resultado.steps.map((step, index) => (
            <li key={`${step.target}-${index}`} className={s.passo}>
              <Badge tone={step.ok ? 'success' : 'danger'}>{step.target}</Badge>
              <span className={s.checkMotivo}>{step.detail ?? t(step.ok ? 'runtimeStatus.deploy.step.ok' : 'runtimeStatus.deploy.step.failed')}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <ConfirmDialog
        open={confirmando}
        title={t('runtimeStatus.deploy.confirm.title')}
        // Frase literal do FR-093.
        message={t('runtimeStatus.deploy.confirm.message', { target: alvo })}
        confirmLabel={t('runtimeStatus.deploy.confirm.confirm')}
        loading={apply.isPending}
        error={
          erro ? (
            // `deployment_inexistente` cita «{namespace}/{slug}», e esses dois não
            // vêm no `ErroData`: quem os conhece é esta tela, e só `errorView`
            // aceita passá-los. Por isso o texto sai daqui, e não do `ErrorBanner`
            // (que ainda não tem por onde receber `dados`).
            <Alert tone="danger" title={t('runtimeStatus.deploy.error.title')}>
              {
                errorView(erro, {
                  escrita: true,
                  dados: {
                    namespace: status.namespace ?? undefined,
                    slug: status.slug ?? undefined,
                  },
                }).texto
              }
            </Alert>
          ) : null
        }
        onConfirm={() => void confirm()}
        onCancel={() => {
          setConfirmando(false);
          setErro(null);
        }}
      />
    </Card>
  );
}
